import React from "react";

type SectionConfigElem = {
	sectionName: string;
	sectionWeight?: number;
	sectionOrder?: number;
};

/*
	Аналог getModulesWeights — возвращает проценты, рассчитанные из sectionWeight
	для тех секций из appSectionsConfig, которые соответствуют текущим panes.	
	
	Алгоритм:
	 - извлекаем ключи из panes (React.key)
	 - для каждого ключа находим запись в appSectionsConfig, где sectionName соответствует key
	 - фильтруем записи с нулевым весом
	 - вычисляем проценты = weight / totalWeight * 100
	 - возвращаем массив процентов в порядке paneKeys (только для найденных и ненулевых записей)
*/

const getComponentsWeights = (
	appSectionsConfig?: SectionConfigElem[],
	panes?: React.ReactNode[],
	aliases?: Record<string, string[]>,
): number[] => {
	if (!appSectionsConfig || appSectionsConfig.length === 0) return [];
	if (!panes || panes.length === 0) return [];

	const paneKeys = panes
		.map((p) => String((p as React.ReactElement)?.key ?? "").trim())
		.filter(Boolean);
	if (paneKeys.length === 0) return [];

	const matchesKey = (
		sectionName: string,
		key: string,
		aliasList: string[] = [],
	) =>
		sectionName === key ||
		sectionName.endsWith(`_${key}`) ||
		sectionName.includes(key) ||
		aliasList.some((a) => sectionName.includes(a));

	// Оставляем из конфига только те записи, которые соответствуют paneKeys и имеют вес > 0
	const configsForPanes = paneKeys
		.map((k) => {
			const cfg = appSectionsConfig.find(
				(c) =>
					c &&
					c.sectionName &&
					matchesKey(c.sectionName, k, aliases?.[k] ?? []) &&
					Number(c.sectionWeight ?? 0) !== 0,
			);
			return cfg ?? null;
		})
		.filter((c): c is SectionConfigElem => c !== null);

	if (configsForPanes.length === 0) return [];

	const totalWeight = configsForPanes.reduce(
		(s, c) => s + Number(c.sectionWeight ?? 0),
		0,
	);
	if (totalWeight <= 0) {
		const equal = 100 / configsForPanes.length;
		return configsForPanes.map(() => Number(equal.toFixed(3)));
	}

	return configsForPanes.map((c) =>
		Number(((Number(c.sectionWeight ?? 0) / totalWeight) * 100).toFixed(3)),
	);
};

export { getComponentsWeights };
